import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from 'react-aria-components'
import { tv, type VariantProps } from 'tailwind-variants'
import { focusRing } from '@/lib/style'
import { cn } from '@/lib/utils'
import styles from './button.module.css'

export type Variants = VariantProps<typeof button>

// Unlike `text`, we don't export the `button` style as public API
// Button component comes with other important stylistic features such as corner decorations, brackets around link text.
export const button = tv({
  extend: focusRing,
  // --border-color is a CSS variable used by the corner decorations (see button.module.css)
  base: `justify-center items-center whitespace-nowrap rounded-sm inline-flex gap-2 [&_svg]:pointer-events-none [&_svg]:size-6
    font-nb-architekt text-sm font-bold tracking-[0.04em] px-3 py-2.5 cursor-pointer
    transition-colors disabled:pointer-events-none disabled:opacity-50`,
  variants: {
    variant: {
      default: cn(
        styles.primary_button,
        'bg-primary text-primary-foreground [--border-color:var(--color-primary)]',
      ),
      outline: 'border border-input bg-transparent text-foreground hover:bg-foreground/5',
      ghost: 'bg-transparent text-foreground hover:bg-primary/10',
      link: 'bg-transparent text-primary',
    },
    size: {
      default: 'min-h-12',
      sm: 'min-h-11 px-0 text-xs tracking-[0.06em]',
      xs: 'min-h-11 px-0 text-[0.625rem] tracking-[0.06em]',
      icon: 'size-11 shrink-0 p-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export interface ButtonProps extends Omit<AriaButtonProps, 'isDisabled'>, Variants {
  children?: React.ReactNode
  disabled?: boolean
}

export const ButtonContent = ({
  variant,
  children,
}: {
  variant: Variants['variant']
  children?: React.ReactNode
}) => (
  <>
    {/* Corner decoration top left + bottom right (primary button only) */}
    {variant === 'default' && (
      <span aria-hidden="true" className={styles.top_left_bottom_right}></span>
    )}
    {/* Bracket to the left of button content (link button only) */}
    {variant === 'link' ? '[' : ''}
    {/* Button content */}
    {children}
    {/* Bracket to the right of button content (link button only) */}
    {variant === 'link' ? ']' : ''}
    {/* Corner decoration top right + bottom left (primary button only) */}
    {variant === 'default' && (
      <span aria-hidden="true" className={styles.top_right_bottom_left}></span>
    )}
  </>
)

/** Brand styling and React Aria interactions, with a native-style disabled prop. */
export function Button({
  className,
  variant = button.defaultVariants.variant,
  size,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      data-slot="button"
      {...props}
      type={type}
      isDisabled={disabled}
      className={composeRenderProps(className, (className, renderProps) =>
        button({
          ...renderProps,
          variant,
          size,
          className,
        }),
      )}
    >
      <ButtonContent variant={variant}>{children}</ButtonContent>
    </AriaButton>
  )
}
